import { supabase } from '@/integrations/supabase/client';
import { Plan } from '@/components/company/types/planTypes';

/**
 * Adds a new plan to the plans table
 * @param pageId The ID of the page to associate the plan with
 * @param planName The name of the plan
 * @param displayName The display name of the plan (defaults to planName if not provided)
 * @param addOn Whether the plan is an add-on (defaults to false)
 * @returns An object with success status and message
 */
export async function addPlan(
  pageId: string,
  planName: string,
  displayName?: string,
  addOn: boolean = false
): Promise<{ success: boolean; message: string; error?: string; plan?: any }> {
  try {
    // Validate inputs
    if (!pageId) {
      return { success: false, message: 'Page ID is required', error: 'Missing page ID' };
    }
    
    if (!planName || planName.trim() === '') {
      return { success: false, message: 'Plan name is required', error: 'Missing plan name' };
    }

    // Create the plan
    const { data, error } = await supabase
      .from('plans')
      .insert({
        page_id: pageId,
        plan_name: planName.trim(),
        display_name: (displayName || planName).trim(),
        add_on: addOn
      })
      .select()
      .single();

    if (error) {
      console.error('Error adding plan:', error);
      return { 
        success: false, 
        message: 'Failed to add plan', 
        error: error.message 
      };
    }

    return { 
      success: true, 
      message: `Successfully added plan "${planName}"`,
      plan: data
    };
  } catch (error: any) {
    console.error('Unexpected error adding plan:', error);
    return { 
      success: false, 
      message: 'An unexpected error occurred', 
      error: error.message || String(error)
    };
  }
}

/**
 * Updates an existing plan with new values
 * @param planId The ID of the plan to update
 * @param updateData The updated plan data (plan_name, display_name, add_on)
 * @returns Object with success status and message
 */
export async function updatePlan(
  planId: string,
  updateData: {
    plan_name?: string;
    display_name?: string;
    add_on?: boolean;
  }
): Promise<{ success: boolean; message: string; updatedPlan?: Plan; error?: string }> {
  try {
    // Validate inputs
    if (!planId) {
      return { success: false, message: 'Plan ID is required', error: 'Missing plan ID' };
    }
    
    // Make sure there's something to update
    if (!updateData || Object.keys(updateData).length === 0) {
      return { 
        success: false, 
        message: 'No update data provided', 
        error: 'Missing update data' 
      };
    }
    
    // Create an update object with only the properties that were provided
    const updateObject: any = {};
    
    if (updateData.plan_name !== undefined) {
      updateObject.plan_name = updateData.plan_name.trim();
    }
    
    if (updateData.display_name !== undefined) {
      updateObject.display_name = updateData.display_name.trim();
    }
    
    if (updateData.add_on !== undefined) {
      updateObject.add_on = updateData.add_on;
    }
    
    // Make sure we're actually updating something
    if (Object.keys(updateObject).length === 0) {
      return { 
        success: false, 
        message: 'No valid update data provided', 
        error: 'Invalid update data' 
      };
    }
    
    console.log(`🔄 [planUtils] Updating plan ${planId}:`, updateObject);
    
    // Update plan in the database
    const { data: updatedPlan, error: updateError } = await supabase
      .from('plans')
      .update(updateObject)
      .eq('id', planId)
      .select('*')
      .single();
    
    if (updateError) {
      console.error('❌ [planUtils] Error updating plan:', updateError);
      return {
        success: false,
        message: 'Failed to update plan',
        error: updateError.message
      };
    }
    
    console.log(`✅ [planUtils] Successfully updated plan ${planId}`, updatedPlan);
    
    return {
      success: true,
      message: `Successfully updated plan`,
      updatedPlan: updatedPlan as Plan
    };
  } catch (error) {
    console.error('❌ [planUtils] Unexpected error in updatePlan:', error);
    return {
      success: false,
      message: 'An unexpected error occurred',
      error: error.message
    };
  }
}

/**
 * Removes a plan from the plans table, but only if there are no charges associated with it.
 * Also deletes related entries in plans_map table.
 *
 * @param planId The ID of the plan to delete
 * @param slug The company slug
 * @returns Object with success status and message
 */
export async function removePlan(
  planId: string,
  slug: string
): Promise<{ success: boolean; message: string; error?: string; hasCharges?: boolean }> {
  if (!planId || !slug) {
    return {
      success: false,
      message: 'Plan ID and company slug are required',
      error: 'Missing required parameters'
    };
  }

  try {
    console.log(`🔄 [planUtils] Checking for charges associated with plan ${planId}`);
    
    // First, check if there are any charges associated with this plan
    const { data: charges, error: chargesError } = await supabase
      .from('charges')
      .select('id')
      .eq('plan_id', planId);
    
    if (chargesError) {
      console.error('❌ [planUtils] Error checking for charges:', chargesError);
      return {
        success: false,
        message: 'Failed to check for associated charges',
        error: chargesError.message
      };
    }
    
    // If there are charges associated with this plan, return an error
    if (charges && charges.length > 0) {
      console.warn(`⚠️ [planUtils] Cannot delete plan ${planId} - has ${charges.length} associated charges`);
      return {
        success: false,
        message: `Cannot delete plan with ${charges.length} associated charges. Please delete the charges first.`,
        hasCharges: true
      };
    }
    
    console.log(`ℹ️ [planUtils] No charges found for plan ${planId}, proceeding with deletion`);
    
    // Delete entries from plans_map
    const { error: mapDeleteError } = await supabase
      .from('plans_map')
      .delete()
      .eq('plan_id', planId);
    
    if (mapDeleteError) {
      console.error('❌ [planUtils] Error deleting from plans_map:', mapDeleteError);
      return {
        success: false,
        message: 'Failed to delete plan mappings',
        error: mapDeleteError.message
      };
    }
    
    console.log(`✅ [planUtils] Deleted mappings for plan ${planId}`);
    
    // Delete the plan itself
    const { error: planDeleteError } = await supabase
      .from('plans')
      .delete()
      .eq('id', planId);
    
    if (planDeleteError) {
      console.error('❌ [planUtils] Error deleting plan:', planDeleteError);
      return {
        success: false,
        message: 'Failed to delete plan',
        error: planDeleteError.message
      };
    }
    
    console.log(`✅ [planUtils] Successfully deleted plan ${planId}`);
    
    return {
      success: true,
      message: `Successfully deleted plan`
    };
  } catch (error) {
    console.error('❌ [planUtils] Unexpected error in removePlan:', error);
    return {
      success: false,
      message: 'An unexpected error occurred',
      error: error.message
    };
  }
}
